import * as React from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DI from "../../utility/DI";

import { formatDate } from "../../utility/tools";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HourglassFullIcon from "@mui/icons-material/HourglassFull";
import PaidIcon from "@mui/icons-material/Paid";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";

import { FetchContext } from "../SideBar";
import { useNavigate } from "react-router-dom";
import BalanceSummary from "./BalanceSummary";
import MobileViewOrders from "../dashboard/MobileViewOrders";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ConfirmDialog from "../../utility/ConfirmDialog";

const iconBlank = <CheckBoxOutlineBlankIcon fontSize="small" />;
const iconChecked = <CheckBoxIcon fontSize="small" />;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const statusTabs = ["false", "true", null, "paid"];

const Pendings = (props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Group-level tab — persist in localStorage
  const [groupTab, setGroupTabState] = React.useState(() => {
    const saved = localStorage.getItem("spent_app_group_tab");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const setGroupTab = (val) => {
    setGroupTabState(val);
    localStorage.setItem("spent_app_group_tab", val);
  };
  const [groups, setGroups] = React.useState([]);
  const [groupsLoading, setGroupsLoading] = React.useState(false);

  // Net balances
  const [balances, setBalances] = React.useState([]);
  const [balancesLoading, setBalancesLoading] = React.useState(false);

  // Status-level tab (inside each group)
  const [statusTab, setStatusTab] = React.useState(2);
  const [loading, setLoading] = React.useState(true);
  const [transaction, setTransactions] = React.useState([]);
  const { refresh } = React.useContext(FetchContext);

  // Search & date filters
  const [searchQuery, setSearchQuery] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [filterOpen, setFilterOpen] = React.useState(false);

  // Create group dialog
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newGroupName, setNewGroupName] = React.useState("");
  const [newGroupDesc, setNewGroupDesc] = React.useState("");
  const [newGroupMembers, setNewGroupMembers] = React.useState([]);
  const [friends, setFriends] = React.useState([]);
  const [createLoading, setCreateLoading] = React.useState(false);

  const [details, setDetails] = React.useState({
    balance: 0,
    till_date: "",
  });

  const {
    di: { GET, POST, urls, error, success, session },
  } = props;

  // Fetch groups
  const fetchGroups = async () => {
    setGroupsLoading(true);
    try {
      const res = await GET(urls.get.get_groups, { user_id: session?.id });
      if (res?.success) {
        setGroups(res.data || []);
      }
    } catch (e) {
      console.error("Failed to fetch groups", e);
    }
    setGroupsLoading(false);
  };

  // After groups load, validate saved groupTab is still in range
  React.useEffect(() => {
    if (groups.length > 0 && groupTab > groups.length) {
      setGroupTab(0);
    }
  }, [groups]);

  // Fetch friends for create group dialog
  const fetchFriends = async () => {
    const res = await POST(urls.get.get_friends);
    if (res?.success) {
      setFriends(res.data || []);
    }
  };

  // Determine current group_id filter
  const getCurrentGroupId = () => {
    if (groupTab === 0) return undefined; // All — no group filter
    return groups[groupTab - 1]?.id;
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    setLoading(true);
    const params = {
      user_id: session?.id,
      approval_status: statusTabs[statusTab],
    };
    const gid = getCurrentGroupId();
    if (gid !== undefined) {
      params.group_id = gid;
    }

    try {
      const res = await GET(urls.get.get_transactions, params);
      if (res?.success) {
        setTransactions(res.data || []);
        setDetails({
          balance: res.total_approved_balance,
          till_date: res.till_date,
        });
      } else {
        error(res?.message ?? "error");
      }
    } catch (er) {
      error("Oops..! session out. Refresh the page");
      console.log(er);
    }
    setLoading(false);
  };

  // Fetch net balances
  const fetchBalances = async () => {
    setBalancesLoading(true);
    const params = { user_id: session?.id };
    const gid = getCurrentGroupId();
    if (gid !== undefined) {
      params.group_id = gid;
    }
    try {
      const res = await GET(urls.get.get_group_balances, params);
      if (res?.success) {
        setBalances(res.balances || []);
      }
    } catch (e) {
      console.error("Failed to fetch balances", e);
    }
    setBalancesLoading(false);
  };

  React.useEffect(() => {
    fetchGroups();
  }, [refresh]);

  React.useEffect(() => {
    fetchTransactions();
    fetchBalances();
  }, [statusTab, groupTab, groups, refresh]);

  // Reset status tab when group changes
  React.useEffect(() => {
    setStatusTab(2);
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
  }, [groupTab]);

  // Filter transactions by search query and date range (client-side)
  const filteredTransactions = React.useMemo(() => {
    let filtered = transaction;

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.expense_detail?.toLowerCase().includes(q) ||
          t.amount?.toString().includes(q) ||
          t.collaborators?.some((c) => c.name?.toLowerCase().includes(q))
      );
    }

    // Date range filter
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter((t) => {
        const d = new Date(t.expense_time || t.created_at);
        return d >= from;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter((t) => {
        const d = new Date(t.expense_time || t.created_at);
        return d <= to;
      });
    }

    return filtered;
  }, [transaction, searchQuery, dateFrom, dateTo]);

  const changestatus = (transaction_id, collaborator_id, curr_approval_status) => {
    setLoading(true);
    POST(urls.post.change_status, {
      transaction_id: transaction_id,
      collabrator_id: collaborator_id,
      approved: curr_approval_status == "false" ? "true" : "false",
    }).then((res) => {
      if (res.success) {
        success(res.message);
        fetchTransactions();
      } else {
        error(res.message);
        setLoading(false);
      }
    });
  };

  const makePaid = (transaction_id) => {
    setLoading(true);
    POST(urls.post.make_paid, {
      transaction_id: transaction_id,
    }).then((res) => {
      if (res.success) {
        success(res.message);
        fetchTransactions();
      } else {
        error(res.message);
        setLoading(false);
      }
    });
  };

  const deleteTransactions = (transaction_id) => {
    setLoading(true);
    POST(urls.post.delete_transaction, {
      transaction_id: transaction_id,
    }).then((res) => {
      if (res.success) {
        success(res.message);
        fetchTransactions();
      } else {
        error(res.message);
        setLoading(false);
      }
    });
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      error("Group name is required");
      return;
    }
    if (newGroupMembers.length === 0) {
      error("Add at least one member");
      return;
    }
    setCreateLoading(true);
    try {
      const res = await POST(urls.post.create_group, {
        name: newGroupName.trim(),
        description: newGroupDesc.trim() || null,
        members: newGroupMembers.map((m) => m.id),
      });
      if (res?.success) {
        success("Group created!");
        setCreateOpen(false);
        setNewGroupName("");
        setNewGroupDesc("");
        setNewGroupMembers([]);
        fetchGroups();
      } else {
        error(res?.message || "Failed to create group");
      }
    } catch (e) {
      error("Failed to create group");
    }
    setCreateLoading(false);
  };

  const [deleteGroupConfirm, setDeleteGroupConfirm] = React.useState({ open: false, groupId: null, groupName: "" });
  const [deleteGroupLoading, setDeleteGroupLoading] = React.useState(false);

  const handleDeleteGroup = async (groupId) => {
    setDeleteGroupLoading(true);
    try {
      const res = await POST(urls.post.delete_group, { group_id: groupId });
      if (res?.success) {
        success("Group deleted");
        setDeleteGroupConfirm({ open: false, groupId: null, groupName: "" });
        setGroupTab(0);
        fetchGroups();
      } else {
        error(res?.message || "Failed to delete group");
      }
    } catch (e) {
      error("Failed to delete group");
    }
    setDeleteGroupLoading(false);
  };

  const currentGroup = groupTab > 0 ? groups[groupTab - 1] : null;

  return (
    <Box sx={{ width: "100%" }}>
      {/* ===== GROUP TABS ===== */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center" }}>
        <Tabs
          value={groupTab}
          onChange={(e, v) => setGroupTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1 }}
        >
          <Tab label="All" icon={<GroupsIcon />} iconPosition="start" />
          {groups.map((g) => (
            <Tab
              key={g.id}
              label={g.name}
              icon={<GroupsIcon fontSize="small" />}
              iconPosition="start"
            />
          ))}
        </Tabs>
        <IconButton
          color="primary"
          onClick={() => {
            fetchFriends();
            setCreateOpen(true);
          }}
          title="Create Group"
          sx={{ mr: 0.5 }}
        >
          <AddIcon />
        </IconButton>
        <IconButton
          id="shared-settings-icon"
          onClick={() => navigate("/panel/manage?section=groups")}
          title="Manage Groups"
          sx={{ mr: 1, color: "text.secondary" }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ===== SEARCH + FILTER ICON ===== */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search expenses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
          }}
        />
        <IconButton
          onClick={() => setFilterOpen(true)}
          color={(statusTab !== 2 || dateFrom || dateTo) ? "primary" : "default"}
          sx={{
            border: "1px solid",
            borderColor: (statusTab !== 2 || dateFrom || dateTo) ? "primary.main" : "divider",
            borderRadius: 1,
            p: 0.8,
          }}
        >
          <FilterListIcon />
        </IconButton>
      </Box>

      {/* Active filter chips */}
      {(statusTab !== 2 || dateFrom || dateTo) && (
        <Box sx={{ px: 2, pb: 1, display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
          {statusTab !== 2 && (
            <Typography variant="caption" sx={{ bgcolor: "primary.main", color: "white", px: 1, py: 0.2, borderRadius: 1 }}>
              {["Pending", "Acknowledged", "All", "Paid"][statusTab]}
            </Typography>
          )}
          {(dateFrom || dateTo) && (
            <Typography variant="caption" sx={{ bgcolor: "action.selected", px: 1, py: 0.2, borderRadius: 1 }}>
              {dateFrom || "..."} → {dateTo || "..."}
            </Typography>
          )}
          <IconButton size="small" onClick={() => { setStatusTab(2); setDateFrom(""); setDateTo(""); }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      {/* Filter Dialog — status + date */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Filters
          <IconButton size="small" onClick={() => setFilterOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}>
          {/* Status */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Status
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {[
                { label: "Pending", icon: <HourglassFullIcon sx={{ fontSize: 16, color: "orange" }} /> },
                { label: "Acknowledged", icon: <DoneAllIcon sx={{ fontSize: 16, color: "green" }} /> },
                { label: "All", icon: null },
                { label: "Paid", icon: <PaidIcon sx={{ fontSize: 16, color: "green" }} /> },
              ].map((item, i) => (
                <Button
                  key={item.label}
                  size="small"
                  variant={statusTab === i ? "contained" : "outlined"}
                  startIcon={item.icon}
                  onClick={() => setStatusTab(i)}
                  sx={{ textTransform: "none" }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Date Range */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Date Range
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStatusTab(2);
              setDateFrom("");
              setDateTo("");
            }}
            color="secondary"
          >
            Clear All
          </Button>
          <Button variant="contained" onClick={() => setFilterOpen(false)}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== NET BALANCE SUMMARY ===== */}
      <BalanceSummary
        balances={balances}
        loading={balancesLoading}
        groupName={currentGroup?.name}
        groupId={currentGroup?.id || null}
        onRefresh={() => fetchBalances()}
      />

      {/* Settlement context note */}
      {balances?.some(b => b.settled_total > 0) && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            px: 1.5,
            py: 0.6,
            borderRadius: 2,
            bgcolor: isDark ? "rgba(59, 130, 246, 0.1)" : "#f0f9ff",
            border: "1px dashed",
            borderColor: isDark ? "rgba(96, 165, 250, 0.4)" : "#93c5fd",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: isDark ? "#93c5fd" : "#475569",
              fontSize: "0.75rem",
              lineHeight: 1.4,
              display: "block",
            }}
          >
            ℹ️ Expense cards show original amounts. Settlements are reflected in the <strong>Net Balances</strong> above.
          </Typography>
        </Box>
      )}

      {/* ===== TRANSACTION LIST ===== */}
      <CustomTabPanel>
        <MobileViewOrders
          key={statusTabs[statusTab] + groupTab + "card"}
          transaction={filteredTransactions}
          formatDate={formatDate}
          loading={loading}
          changestatus={changestatus}
          deleteTransactions={
            statusTabs[statusTab] !== "paid" ? deleteTransactions : false
          }
          makePaid={(t_id) => {
            if (statusTabs[statusTab] === "true") makePaid(t_id);
            else error("Not yet acknowledged by all collaborators.");
          }}
          transactionType={statusTabs[statusTab]}
          title="Transactions"
        />
      </CustomTabPanel>

      {/* ===== CREATE GROUP DIALOG ===== */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Expense Group</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Group Name"
            placeholder="e.g. Rishikesh Trip"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Description (optional)"
            placeholder="e.g. May 2026 trip expenses"
            value={newGroupDesc}
            onChange={(e) => setNewGroupDesc(e.target.value)}
            fullWidth
            size="small"
          />
          <Autocomplete
            multiple
            size="small"
            options={friends}
            getOptionLabel={(o) => o.firstname}
            value={newGroupMembers}
            onChange={(e, v) => setNewGroupMembers(v)}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={iconBlank}
                  checkedIcon={iconChecked}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.firstname}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Add Members" placeholder="Select friends" />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Confirm Dialog */}
      <ConfirmDialog
        open={deleteGroupConfirm.open}
        onClose={() => setDeleteGroupConfirm({ open: false, groupId: null, groupName: "" })}
        title="Confirm Delete Group"
        content={`Are you sure you want to delete the shared group "${deleteGroupConfirm.groupName || ""}"? Transactions will be preserved but unlinked from this group.`}
        confirmText="Delete"
        confirmColor="error"
        loading={deleteGroupLoading}
        onConfirm={() => deleteGroupConfirm.groupId && handleDeleteGroup(deleteGroupConfirm.groupId)}
      />
    </Box>
  );
};

export default DI(Pendings);
